import get from 'lodash/get';
import moment from 'moment';

const DEFAULT_FRONTEND_EXPORT_FILE_NAME = 'Frontend Export.csv';
const DEFAULT_FRONTEND_EXPORT_DATE_FORMAT = 'DD/MM/YYYY HH:mm:ss';

const SUPPORTED_FILE_TYPES = ['csv', 'xls'];
const DATE_VALUE_PATTERN = /^\d{4}-\d{2}-\d{2}(?:[T\s]\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?(?:Z|[+-]\d{2}:?\d{2})?)?$/;
const DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

function getExportConfig(column) {
  if (!column || column.export === false) return null;
  return column.export && typeof column.export === 'object' ? column.export : {};
}

function getColumnProp(column) {
  if (!column) return '';
  const config = getExportConfig(column) || {};
  return column.prop || config.prop || '';
}

function isExportOnlyColumn(column, prop) {
  return Boolean(column && prop && prop.indexOf('__') === 0);
}

function translate(value, context) {
  if (value === null || value === undefined) return '';
  return context && typeof context.translate === 'function' ? context.translate(value) : value;
}

function isDateValue(value) {
  if (value instanceof Date) return !Number.isNaN(value.getTime());
  return typeof value === 'string' && DATE_VALUE_PATTERN.test(value.trim());
}

function resolveOptionValue(value, config, context) {
  const optionSource = config.options || config.bjGet;
  const options = typeof optionSource === 'function' ? optionSource() : optionSource;
  const optionList = Array.isArray(options) ? options : [];
  const option = optionList.find(item => {
    const optionValue = item && item.value;
    return optionValue == value || (Array.isArray(optionValue) && optionValue.some(itemValue => itemValue == value));
  });
  if (!option) {
    if (config.optionFallback === 'raw') return value;
    return config.optionFallback === undefined ? '' : config.optionFallback;
  }
  return config.translate ? translate(option.label, context) : option.label;
}

function isColumnVisible(column, config, context) {
  if (!config || !column || column.prop === 'bjTableAction') return false;
  if (column.isCheck === false) return false;

  const prop = config.prop || column.prop;
  if (!prop || prop === 'bjTableAction') return false;

  const roles = [].concat(config.roles || []);
  if (roles.length && !roles.includes(context.role)) return false;
  return true;
}

function resolveColumnValue(row, rowIndex, column, config, context, autoFormatDate) {
  const prop = config.prop || column.prop;
  let value = get(row, prop);

  if (typeof config.formatter === 'function') {
    value = config.formatter(value, row, column, rowIndex);
    return value === null || value === undefined ? '' : value;
  }

  const hasOptions = Boolean(config.options || config.bjGet);
  if (hasOptions) {
    value = resolveOptionValue(value, config, context);
  }

  const shouldFormatDate = config.type === 'date' ||
    Boolean(config.dateFormat) ||
    (!hasOptions && autoFormatDate && isDateValue(value));
  if (shouldFormatDate && value !== null && value !== undefined && value !== '') {
    const date = moment(value);
    if (date.isValid()) {
      const autoDateFormat = typeof value === 'string' && DATE_ONLY_PATTERN.test(value.trim())
        ? 'DD/MM/YYYY'
        : DEFAULT_FRONTEND_EXPORT_DATE_FORMAT;
      value = date.format(config.dateFormat || autoDateFormat);
    }
  }

  return value === null || value === undefined ? '' : value;
}

export function normalizeFrontendExportFileName(fileName) {
  const rawName = String(fileName || DEFAULT_FRONTEND_EXPORT_FILE_NAME).trim();
  const safeName = rawName.replace(/[<>:"/\\|?*\u0000-\u001F]/g, '_');
  const extensionMatch = safeName.match(/\.([^.]+)$/);
  if (!extensionMatch) return `${safeName || 'Frontend Export'}.csv`;

  const extension = extensionMatch[1].toLowerCase();
  if (!SUPPORTED_FILE_TYPES.includes(extension)) {
    throw new Error(`Unsupported export file type: ${extension}`);
  }
  return safeName;
}

export function resolveFrontendExportLabelList(labelList, orderedLabelList) {
  const source = Array.isArray(labelList) ? labelList : [];
  const ordered = Array.isArray(orderedLabelList) ? orderedLabelList : [];
  if (!source.length || !ordered.length) return source;

  const orderedProps = ordered
    .map(column => getColumnProp(column))
    .filter(prop => prop && prop !== 'bjTableAction');
  if (!orderedProps.length) return source;

  const orderedPropSet = new Set(orderedProps);
  const sourceColumns = source
    .map(column => ({ column, prop: getColumnProp(column), config: getExportConfig(column) }))
    .filter(item => item.config && item.prop && item.prop !== 'bjTableAction');

  const matchedSourceColumns = sourceColumns.filter(item => orderedPropSet.has(item.prop));
  if (!matchedSourceColumns.length) {
    return source;
  }

  const sourceColumnMap = matchedSourceColumns.reduce((map, item) => {
    if (!map.has(item.prop)) map.set(item.prop, []);
    map.get(item.prop).push(item.column);
    return map;
  }, new Map());

  const orderedColumns = ordered
    .filter(column => column && column.isCheck !== false)
    .map(column => {
      const sourceColumns = sourceColumnMap.get(getColumnProp(column));
      const sourceColumn = Array.isArray(sourceColumns) ? sourceColumns.shift() : null;
      return sourceColumn ? { ...sourceColumn, isCheck: column.isCheck } : null;
    })
    .filter(Boolean);

  let orderedColumnsInserted = false;
  const resolvedColumns = sourceColumns.reduce((columns, item) => {
    if (orderedPropSet.has(item.prop)) {
      if (!orderedColumnsInserted) {
        columns.push(...orderedColumns);
        orderedColumnsInserted = true;
      }
      return columns;
    }

    if (isExportOnlyColumn(item.column, item.prop)) {
      columns.push(item.column);
    }

    return columns;
  }, []);

  return resolvedColumns;
}

export function buildFrontendExportData(labelList, rows, context = {}, options = {}) {
  const columns = (labelList || [])
    .map(column => ({ column, config: getExportConfig(column) }))
    .filter(item => isColumnVisible(item.column, item.config, context));

  if (!columns.length) throw new Error('No exportable columns were found in labelList');

  const headers = columns.map(({ column, config }) => {
    const label = column.label === null || column.label === undefined
      ? (config.prop || column.prop)
      : column.label;
    return options.translateLabel ? translate(label, context) : label;
  });
  const data = (rows || []).map((row, rowIndex) => columns.map(item => (
    resolveColumnValue(
      row,
      rowIndex,
      item.column,
      item.config,
      context,
      options.autoFormatDate !== false,
    )
  )));
  return { headers, data };
}
